'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Users, Plus, BookOpen, Utensils, Link2, FileText, Package, Loader2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { RecipeIcon, DiscoverIcon, DashboardIcon, ShoppingIcon, MealPlannerIcon } from './icons'
import { Button } from '@material-tailwind/react'
import { useAppSelector } from 'features/hooks'
import { canUseShopping } from '@/lib/shoppingLists/access'
import { canUseMealPlanner } from '@/lib/mealPlanner/access'
import { RootState } from 'features/store'
import { isUserAvailable } from 'helpers/authUser'
import RecipeImportModal from './recipe/RecipeImportModal'

interface MobileNavigationProps {
  isAuthenticated: boolean
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ isAuthenticated }) => {
  const pathname = usePathname()
  const router = useRouter()
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [shoppingPending, setShoppingPending] = useState(false)
  const { user } = useAppSelector((state: RootState) => state.culineerAuth)
  const userAvailable = isUserAvailable(user)
  const isMarketOrProducer = ['market', 'producer'].includes(user?.user_type?.toLowerCase() || '')
  const isProducer = user?.user_type?.toLowerCase() === 'producer'
  const canAddFeedPost =
    isMarketOrProducer || user?.user_type?.toLowerCase() === 'general'

  const isNavbarScreen = pathname.startsWith('/markets') ||
                        pathname.startsWith('/producers') ||
                        pathname.startsWith('/homecooks') ||
                        pathname.startsWith('/directory') ||
                        pathname.startsWith('/privacy') ||
                        pathname.startsWith('/terms-of-service') ||
                        pathname === '/' ||
                        pathname === '/auth/login' ||
                        pathname === '/auth/register' ||
                        pathname === '/auth/forgot-password' ||
                        pathname === '/auth/reset-password' ||
                        pathname === '/auth/sign-in-link' ||
                        pathname === '/auth/verify-email' ||
                        pathname.startsWith('/auth/join-culineer')

  const isModalScreen = pathname.startsWith('/pricing') ||
                       pathname.startsWith('/import') ||
                       pathname.startsWith('/register') ||
                       pathname.startsWith('/welcome') ||
                       pathname.startsWith('/Delete_user') ||
                       pathname.startsWith('/download') ||
                       pathname.startsWith('/form')

  // The spinner ends when the page changes; the timer is a safety net for a navigation that never lands.
  useEffect(() => {
    if (!shoppingPending) return
    const timer = window.setTimeout(() => setShoppingPending(false), 20000)
    return () => window.clearTimeout(timer)
  }, [shoppingPending])
  useEffect(() => {
    setShoppingPending(false)
  }, [pathname])

  useEffect(() => {
    document.body.style.overflow = addMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [addMenuOpen])

  const handleImportSuccess = useCallback((scrapedData: any) => {
    sessionStorage.setItem('importedRecipeData', JSON.stringify(scrapedData))
    router.push('/recipes/add?imported=true')
  }, [router])

  if (isNavbarScreen || isModalScreen) return null

  const isActive = (path: string) => {
    if (path === '/recipes') return pathname.startsWith('/recipes')
    if (path === '/meal-planner') return pathname.startsWith('/meal-planner')
    if (path === '/blog') return pathname.startsWith('/blog')
    if (path === '/shopping-lists') return pathname.startsWith('/shopping-lists')
    return pathname === path
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 sm:hidden pb-[env(safe-area-inset-bottom)]">
        <div className="flex flex-col items-center justify-center py-4 px-4">
          <div className="text-center mb-3">
            <p className="text-sm text-gray-600 mb-1">Join our Culineer community!</p>
            <p className="text-xs text-gray-500">Unlock all features to share culinary creations</p>
          </div>
          <Button
            size="sm"
            className="bg-[#85C349] text-white shadow-md transition-all duration-300 ease-in-out hover:scale-105 hover:bg-[#75B339] hover:shadow-lg rounded-full px-6"
            onClick={() => router.push('/auth/login')}
          >
            Sign In
          </Button>
        </div>
      </div>
    )
  }

  // Farm and market accounts get this in place of Shopping, in the middle of the bar:
  // farms: Dashboard, Discover, +, Cooking, Community; markets: Discover, Cooking, +, Community, Learn.
  const plusButton = (
            <div className="flex flex-1 flex-col items-center justify-center min-h-[52px]">
              <button
                onClick={() => setAddMenuOpen(!addMenuOpen)}
                className={`flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 ${
                  addMenuOpen
                    ? 'bg-[#75B339] rotate-45 scale-95'
                    : 'bg-[#85C349] hover:bg-[#75B339]'
                }`}
                aria-label="Quick actions"
              >
                <Plus size={22} className="text-white" />
              </button>
            </div>
  )

  return (
    <>
      {/* Backdrop */}
      {addMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          onClick={() => setAddMenuOpen(false)}
        />
      )}

      {/* Quick action menu */}
      {addMenuOpen && (
        <div className="fixed bottom-[68px] left-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-2xl bg-white shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Create</p>
          </div>

          <button
            onClick={() => { setAddMenuOpen(false); router.push('/recipes/add') }}
            className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-gray-50 active:bg-gray-100 border-b border-gray-50 transition-colors"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
              <Utensils size={18} />
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800">Add Recipe</p>
              <p className="text-xs text-gray-500">Type it in manually</p>
            </div>
          </button>

          <button
            onClick={() => { setAddMenuOpen(false); setImportModalOpen(true) }}
            className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
            style={{ borderBottom: canAddFeedPost ? '1px solid #f9fafb' : undefined }}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <Link2 size={18} />
            </span>
            <div>
              <p className="text-sm font-medium text-gray-800">Import Recipe</p>
              <p className="text-xs text-gray-500">Paste a link</p>
            </div>
          </button>

          {canAddFeedPost && (
            <button
              onClick={() => { setAddMenuOpen(false); router.push('/create-post') }}
              className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
              style={{ borderBottom: isProducer ? '1px solid #f9fafb' : undefined }}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-500">
                <FileText size={18} />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800">Add Post</p>
                <p className="text-xs text-gray-500">Share an update</p>
              </div>
            </button>
          )}

          {isProducer && (
            <button
              onClick={() => { setAddMenuOpen(false); router.push('/dashboard/products') }}
              className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-500">
                <Package size={18} />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-800">Add Product</p>
                <p className="text-xs text-gray-500">List something you grow or make</p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Bottom nav bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 sm:hidden pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around px-1 pt-2 pb-3">

          {isMarketOrProducer && user?.user_type?.toLowerCase() === 'producer' && (
            <Link
              href="/dashboard"
              className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
                isActive('/dashboard') ? 'text-[#85C349]' : 'text-gray-500'
              }`}
            >
              <DashboardIcon width={24} height={24} />
              <span className="text-[10px] font-medium">Dashboard</span>
            </Link>
          )}

          {/* Discover */}
          <Link
            href="/discover"
            className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
              isActive('/discover') ? 'text-[#85C349]' : 'text-gray-500'
            }`}
          >
            <DiscoverIcon width={24} height={24} />
            <span className="text-[10px] font-medium">Discover</span>
          </Link>

          {/* Quick actions: centered for farms */}
          {isProducer && plusButton}

          {/* Cooking */}
          <Link
            href="/recipes"
            className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
              isActive('/recipes') ? 'text-[#85C349]' : 'text-gray-500'
            }`}
          >
            <RecipeIcon width={24} height={24} />
            <span className="text-[10px] font-medium">Cooking</span>
          </Link>

          {canUseMealPlanner(user) && (
            <Link
              href="/meal-planner"
              className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
                isActive('/meal-planner') ? 'text-[#85C349]' : 'text-gray-500'
              }`}
            >
              <MealPlannerIcon width={24} height={24} />
              <span className="text-[10px] font-medium">Planner</span>
            </Link>
          )}

          {/* Center slot: home cooks get Shopping; farm and market accounts (no shopping
              lists) get the quick-actions "+" button that opens the Create menu above. */}
          {!canUseShopping(user) && !isProducer && plusButton}
          {canUseShopping(user) && (
            <Link
              href="/shopping-lists"
              aria-busy={shoppingPending}
              onClick={(event) => {
                // A slow navigation looks like a dead button; show progress and ignore repeat taps.
                if (shoppingPending) {
                  event.preventDefault()
                  return
                }
                if (!pathname.startsWith('/shopping-lists')) setShoppingPending(true)
              }}
              className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
                isActive('/shopping-lists') || shoppingPending ? 'text-[#85C349]' : 'text-gray-500'
              }`}
            >
              {shoppingPending ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <ShoppingIcon width={24} height={24} />
              )}
              <span className="text-[10px] font-medium">Shopping</span>
            </Link>
          )}

          {/* Community */}
          <Link
            href="/community"
            className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
              isActive('/community') ? 'text-[#85C349]' : 'text-gray-500'
            }`}
          >
            <Users size={22} />
            <span className="text-[10px] font-medium">Community</span>
          </Link>

          {user?.user_type?.toLowerCase() !== 'producer' && (
            <Link
              href="/blog"
              className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-[52px] rounded-lg transition-colors ${
                isActive('/blog') ? 'text-[#85C349]' : 'text-gray-500'
              }`}
            >
              <BookOpen size={22} />
              <span className="text-[10px] font-medium">Learn</span>
            </Link>
          )}

        </div>
      </div>

      <RecipeImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  )
}

export default MobileNavigation
